import { useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import { Loader } from "@/components/shared";
import { client, appwriteConfig, databases } from "@/lib/appwrite/config";
import { useGetCurrentUser, useGetDealPages } from "@/lib/react-query/queries";
import { getMultipleUsersByIds } from "@/lib/appwrite/api";
import { DealDocument } from "@/types";
import { Query } from "appwrite";
import { AlertTriangle, MessageSquare, RefreshCw } from "lucide-react";

type ChatMetadata = {
  lastMessage?: string;
  lastMessageTime?: string;
  unreadCount: number;
};

type DealWithUser = DealDocument & {
  otherUser?: {
    id: string;
    name: string;
    imageUrl?: string;
  };
  chatMetadata?: ChatMetadata;
};

interface ChatListProps {
  onSelectDeal: (deal: DealWithUser) => void;
}

const statusConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  negotiation: { label: "Negotiating", color: "text-amber-400", bgColor: "bg-amber-500/20" },
  "payment-requested": { label: "Payment Requested", color: "text-purple-400", bgColor: "bg-purple-500/20" },
  paid: { label: "Paid", color: "text-blue-400", bgColor: "bg-blue-500/20" },
  "delivery-details-added": { label: "Address Added", color: "text-cyan-400", bgColor: "bg-cyan-500/20" },
  "in-progress": { label: "In Progress", color: "text-indigo-400", bgColor: "bg-indigo-500/20" },
  shipped: { label: "Shipped", color: "text-sky-400", bgColor: "bg-sky-500/20" },
  delivered: { label: "Delivered", color: "text-emerald-400", bgColor: "bg-emerald-500/20" },
  completed: { label: "Completed", color: "text-gray-400", bgColor: "bg-gray-500/20" },
  "dispute-raised": { label: "Dispute", color: "text-red-400", bgColor: "bg-red-500/20" },
  "payment-processing": { label: "Processing", color: "text-purple-400", bgColor: "bg-purple-500/20" },
};

const formatTime = (dateString?: string): string => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;

  return date.toLocaleDateString();
};

const truncateMessage = (message: string, maxLength: number = 40): string => {
  if (!message) return "";
  if (message.length <= maxLength) return message;
  return message.substring(0, maxLength) + "...";
};

const DealItem = memo(({
  deal,
  currentUserId,
  isAgent,
  onSelect
}: {
  deal: DealWithUser;
  currentUserId: string;
  isAgent: boolean;
  onSelect: (deal: DealWithUser) => void;
}) => {
  const role = isAgent ? "monitoring" : deal.buyerId === currentUserId ? "payer" : "provider";
  const hasUnread = (deal.chatMetadata?.unreadCount || 0) > 0;
  const displayTime = deal.chatMetadata?.lastMessageTime || deal.updatedAt;
  const config = statusConfig[deal.status] || { label: deal.status, color: "text-gray-400", bgColor: "bg-gray-500/20" };

  return (
    <div
      onClick={() => onSelect(deal)}
      className={`flex items-start p-4 cursor-pointer transition-all border-b border-gray-700/50 hover:bg-gray-700/50 ${
        hasUnread ? 'bg-blue-900/20' : ''
      }`}
    >
      {/* Avatar */}
      <div className="flex-shrink-0 mr-3">
        {deal.otherUser?.imageUrl ? (
          <img
            src={deal.otherUser.imageUrl}
            alt={deal.otherUser.name}
            className={`w-12 h-12 rounded-full object-cover ${hasUnread ? 'ring-2 ring-blue-500' : ''}`}
          />
        ) : (
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-semibold ${
            hasUnread ? 'bg-blue-600' : 'bg-gray-600'
          }`}>
            {(deal.otherUser?.name || 'U').charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      {/* Deal Info */}
      <div className="flex-1 min-w-0 mr-2">
        {/* Name and Role */}
        <div className="flex items-center gap-2 mb-1">
          <span className={`font-semibold truncate ${hasUnread ? 'text-white' : 'text-gray-200'}`}>
            {deal.otherUser?.name || "Unknown User"}
          </span>
          <span className="text-xs text-gray-500">({role})</span>
        </div>

        {/* Last Message or Amount */}
        {deal.chatMetadata?.lastMessage ? (
          <p className={`text-sm truncate mb-1.5 ${hasUnread ? 'text-gray-200 font-medium' : 'text-gray-400'}`}>
            {truncateMessage(deal.chatMetadata.lastMessage)}
          </p>
        ) : (
          <div className="flex items-center gap-1 mb-1.5">
            <span className="text-sm text-gray-500">Amount:</span>
            <span className="text-sm font-semibold text-emerald-400">
              ₹{deal.requestedAmount || deal.amount || "0"}
            </span>
          </div>
        )}

        {/* Status Badge */}
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
          {config.label}
        </span>
      </div>

      {/* Time and Unread Badge */}
      <div className="flex flex-col items-end flex-shrink-0">
        <span className={`text-xs mb-1.5 ${hasUnread ? 'text-blue-400 font-semibold' : 'text-gray-500'}`}>
          {formatTime(displayTime)}
        </span>
        {hasUnread && (
          <span className="bg-blue-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
            {deal.chatMetadata!.unreadCount > 99 ? "99+" : deal.chatMetadata!.unreadCount}
          </span>
        )}
      </div>
    </div>
  );
});

const ChatList: React.FC<ChatListProps> = ({ onSelectDeal }) => {
  const { data: currentUser } = useGetCurrentUser();
  const currentUserId = currentUser?.$id || currentUser?.id || "";
  const isAgent = Boolean(currentUser?.isSupportAgent);

  const {
    data: dealPages,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetDealPages(currentUserId, isAgent);
  const deals = useMemo(
    () => dealPages?.pages.flatMap((page) => page.documents) ?? [],
    [dealPages]
  );
  const [dealsWithUsers, setDealsWithUsers] = useState<DealWithUser[]>([]);
  const [isLoadingDealsData, setIsLoadingDealsData] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const usersByIdRef = useRef(new Map<string, any>());
  const chatMetadataByDealRef = useRef(new Map<string, ChatMetadata>());

  // Fetch chat metadata (unread count, last message) for a deal
  const fetchChatMetadata = useCallback(
    async (otherUserId: string, dealId?: string): Promise<ChatMetadata> => {
      try {
        let unreadResponse;
        let lastMessageResponse;

        if (dealId) {
          // Filter by dealId for proper deal-specific messages
          unreadResponse = await databases.listDocuments(
            appwriteConfig.databaseId,
            appwriteConfig.messagesCollectionId,
            [
              Query.equal("dealId", dealId),
              Query.equal("sender_id", otherUserId),
              Query.equal("recipient_id", currentUserId),
              Query.equal("is_read", false),
            ]
          );

          lastMessageResponse = await databases.listDocuments(
            appwriteConfig.databaseId,
            appwriteConfig.messagesCollectionId,
            [
              Query.equal("dealId", dealId),
              Query.orderDesc("$createdAt"),
              Query.limit(1),
            ]
          );
        } else {
          // Legacy behavior for deals without dealId
          unreadResponse = await databases.listDocuments(
            appwriteConfig.databaseId,
            appwriteConfig.messagesCollectionId,
            [
              Query.equal("sender_id", otherUserId),
              Query.equal("recipient_id", currentUserId),
              Query.equal("is_read", false),
            ]
          );

          lastMessageResponse = await databases.listDocuments(
            appwriteConfig.databaseId,
            appwriteConfig.messagesCollectionId,
            [
              Query.or([
                Query.and([
                  Query.equal("sender_id", currentUserId),
                  Query.equal("recipient_id", otherUserId),
                ]),
                Query.and([
                  Query.equal("sender_id", otherUserId),
                  Query.equal("recipient_id", currentUserId),
                ]),
              ]),
              Query.orderDesc("$createdAt"),
              Query.limit(1),
            ]
          );
        }

        const lastMsg = lastMessageResponse.documents[0];

        return {
          unreadCount: unreadResponse.total,
          lastMessage: lastMsg?.message || undefined,
          lastMessageTime: lastMsg?.timestamp || lastMsg?.$createdAt || undefined,
        };
      } catch (error) {
        console.error("Error fetching chat metadata:", error);
        return { unreadCount: 0 };
      }
    },
    [currentUserId]
  );

  // Fetch user info and chat metadata for each deal
  const fetchDealsData = useCallback(async () => {
    if (!deals || deals.length === 0) {
      setDealsWithUsers([]);
      setIsLoadingDealsData(false);
      return;
    }

    const requiredUserIds = isAgent
      ? deals.flatMap((deal) => [deal.buyerId, deal.sellerId])
      : deals.map((deal) => deal.buyerId === currentUserId ? deal.sellerId : deal.buyerId);
    const missingUserIds = [...new Set(requiredUserIds)].filter((id) => !usersByIdRef.current.has(id));
    if (missingUserIds.length > 0) {
      const users = await getMultipleUsersByIds(missingUserIds);
      users.forEach((user: any) => usersByIdRef.current.set(user.$id ?? user.id, user));
    }

    const dealsWithUserInfo = await Promise.all(
      deals.map(async (deal) => {
        const otherUserId = deal.buyerId === currentUserId ? deal.sellerId : deal.buyerId;

        let otherUser = {
          id: otherUserId,
          name: "Unknown User",
          imageUrl: undefined as string | undefined,
        };

        try {
          if (isAgent) {
            const buyer = usersByIdRef.current.get(deal.buyerId);
            const seller = usersByIdRef.current.get(deal.sellerId);
            otherUser = {
              id: deal.buyerId,
              name: `${buyer?.name || "Buyer"} ↔ ${seller?.name || "Seller"}`,
              imageUrl: buyer?.imageUrl,
            };
          } else {
            const otherUserData = usersByIdRef.current.get(otherUserId);
            if (otherUserData) {
              otherUser = {
                id: otherUserData.$id,
                name: otherUserData.name,
                imageUrl: otherUserData.imageUrl,
              };
            }
          }
        } catch (error) {
          console.error("Error fetching user:", error);
        }

        // The monitoring list is already sorted by deal activity. Loading two
        // message queries for every monitored deal caused an N+1 request storm.
        let chatMetadata = chatMetadataByDealRef.current.get(deal.$id);
        if (!chatMetadata) {
          chatMetadata = isAgent
            ? { unreadCount: 0 }
            : await fetchChatMetadata(otherUserId, deal.$id);
          chatMetadataByDealRef.current.set(deal.$id, chatMetadata);
        }

        return {
          ...deal,
          otherUser,
          chatMetadata,
        };
      })
    );

    // Sort by last message time (most recent first)
    const sortedDeals = dealsWithUserInfo.sort((a, b) => {
      const timeA = a.chatMetadata?.lastMessageTime || a.updatedAt;
      const timeB = b.chatMetadata?.lastMessageTime || b.updatedAt;
      return new Date(timeB).getTime() - new Date(timeA).getTime();
    });

    setDealsWithUsers(sortedDeals);
    setIsLoadingDealsData(false);
  }, [deals, currentUserId, fetchChatMetadata, isAgent]);

  // Initial load
  useEffect(() => {
    if (deals || isError) {
      fetchDealsData();
    }
  }, [deals, isError, fetchDealsData]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) void fetchNextPage();
    }, { rootMargin: "160px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, dealsWithUsers.length]);

  // Real-time subscription for new messages
  useEffect(() => {
    if (!currentUserId) return;

    const channel = `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.messagesCollectionId}.documents.user.${currentUserId}`;

    const unsubscribe = client.subscribe(channel, (response: any) => {
      if (!response.payload) return;

      const messageDoc = response.payload;
      const isRelevant =
        isAgent ||
        messageDoc.sender_id === currentUserId ||
        messageDoc.recipient_id === currentUserId;

      if (isRelevant) {
        if (!isAgent) {
          const messageDealId = messageDoc.dealId ?? messageDoc.deal_id;
          if (messageDealId) chatMetadataByDealRef.current.delete(messageDealId);
          else chatMetadataByDealRef.current.clear();
          void fetchDealsData();
        }
      }
    });

    return () => unsubscribe();
  }, [currentUserId, fetchDealsData, isAgent]);

  // Manual refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      usersByIdRef.current.clear();
      chatMetadataByDealRef.current.clear();
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  if ((isLoading || isLoadingDealsData) && !isRefreshing) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-800">
        <Loader />
        <p className="mt-3 text-sm text-gray-400">Loading chats...</p>
      </div>
    );
  }

  return (
    <div className="chat-list w-full bg-gray-800 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <h2 className="text-lg font-semibold text-white">
          {isAgent ? "Active Conversations" : "Deal Chats"}
        </h2>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="p-2 rounded-full hover:bg-gray-700 transition-colors text-gray-400 hover:text-white disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Disclaimer Banner */}
      <div className="flex items-start gap-2 px-4 py-3 bg-amber-500/10 border-b border-amber-500/20">
        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-200/80 leading-relaxed">
          Do not share personal contact information or conduct deals outside Busiconn.
          Violations may result in account suspension.
        </p>
      </div>

      {/* Empty State */}
      {(!dealsWithUsers || dealsWithUsers.length === 0) ? (
        <div className="flex flex-col items-center justify-center flex-1 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center mb-4">
            <MessageSquare className="w-8 h-8 text-gray-500" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">No Deal Chats Yet</h3>
          <p className="text-sm text-gray-400 max-w-xs">
            Your deal conversations will appear here when you start negotiating with sellers or buyers.
          </p>
        </div>
      ) : (
        /* Deal List */
        <div className="flex-1 overflow-y-auto">
          {dealsWithUsers.map((deal) => (
            <DealItem
              key={deal.$id}
              deal={deal}
              currentUserId={currentUserId}
              isAgent={isAgent}
              onSelect={onSelectDeal}
            />
          ))}
          <div ref={loadMoreRef} className="h-10 flex items-center justify-center">
            {isFetchingNextPage && <Loader />}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatList;
